import type {
  DefaultContext,
  DefaultOptions,
  FetchResult,
  MutationOptions,
  NormalizedCacheObject,
  OperationVariables,
} from '@apollo/client';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { getPublicEnv } from '@UI/components/PublicEnv/public-env';
import { getAllocationAtClient, getTokenAtClient } from '@controls/utils/cookie';
import Cookies from 'js-cookie';
import { appKeysConfig } from '../../@controls/constants/common';

export const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const httpLink = createUploadLink({
  uri: getPublicEnv('GRAPHQL_ENDPOINT'),
});

const authLink = setContext((_, { headers = {} }) => {
  const token = getTokenAtClient();
  const allocation = getAllocationAtClient();
  return {
    headers: {
      authorization: token ? `Bearer ${token}` : '',
      allocation: allocation,
      clientTimezone,
      ...headers,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  try {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        console.error('GraphQL error:>>>>> ', err);
        switch (err.message) {
          case 'HTTP_TOKEN_INVALID':
            Cookies.remove(appKeysConfig.TOKEN);
            Cookies.remove(appKeysConfig.USER_ACTIONS);
            Cookies.remove(appKeysConfig.ALLOCATION);
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            forward(operation);
            break;
        }
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
    }
  } catch (error) {
    console.log('errorLink >>>', error);
  }
});

class CustomClient extends ApolloClient<NormalizedCacheObject> {
  async mutate<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
    TContext extends Record<string, any> = DefaultContext,
  >(options: MutationOptions<TData, TVariables, TContext>): Promise<FetchResult<TData>> {
    const result = await super.mutate<TData, TVariables, TContext>({
      ...options,
    });
    await this.clearStore();
    return result;
  }
}

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
};

export const graphqlClient = new CustomClient({
  cache: new InMemoryCache({
    // resultCaching: false,
  }),
  ssrMode: true,
  link: ApolloLink.from([errorLink, authLink, httpLink as unknown as ApolloLink]),
  defaultOptions: defaultOptions,
});
